import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import Pagination from "../components/Pagination";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";

// EMPTY FORM

const EMPTY_FORM = {
  insitem_name: "",
  insitem_type: "1",
  insitem_desc: "",
};

export default function ManageInsurance() {
  // DATA

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // SEARCH

  const [search, setSearch] = useState("");

  // PAGINATION

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // FORM

  const [form, setForm] = useState({
    ...EMPTY_FORM,
  });

  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // MODALS

  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);

  const [delSaving, setDelSaving] = useState(false);

  // FETCH DATA

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/manage-insurance", {
        params: {
          page,
          limit,
          search,
        },
      });

      const responseData = response.data;

      if (Array.isArray(responseData)) {
        setData(responseData);

        setTotal(responseData.length);

        setTotalPages(Math.ceil(responseData.length / limit));
      } else {
        setData(responseData.data || []);

        setTotal(Number(responseData.total || 0));

        setTotalPages(
          Number(
            responseData.totalPages ||
              Math.ceil(Number(responseData.total || 0) / limit)
          )
        );
      }
    } catch (error) {
      console.error("fetchData error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load insurance items"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // SEARCH

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // RESET FORM

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // OPEN ADD MODAL

  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // CLOSE FORM MODAL

  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // EDIT

  const startEdit = (row) => {
    setForm({
      insitem_name: row.insitem_name || "",
      insitem_type: String(row.insitem_type ?? "1"),
      insitem_desc: row.insitem_desc || "",
    });

    setEditId(row.insitem_id);
    setFormModal(true);
  };

  // VIEW

  const handleView = (row) => {
    setViewModal(row);
  };

  // FORM CHANGE

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // SUBMIT

  const handleSubmit = async () => {
    if (!form.insitem_name.trim()) {
      toasts.error("Insurance name is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        insitem_name: form.insitem_name.trim(),
        insitem_type: Number(form.insitem_type),
        insitem_desc: form.insitem_desc.trim(),
      };

      if (editId) {
        await API.put(`/manage-insurance/${editId}`, payload);

        toasts.success("Insurance updated successfully");
      } else {
        await API.post("/manage-insurance", payload);

        toasts.success("Insurance added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData();
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // DELETE

  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/manage-insurance/${delModal.insitem_id}`);

      toasts.success("Insurance deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // SEQUENCE UPDATE

  const handleSequenceChange = async (id, value) => {
    try {
      await API.put(`/manage-insurance/${id}/sequence`, {
        seq: Number(value),
      });

      toasts.success("Sequence updated successfully");

      await fetchData();
    } catch (error) {
      console.error("Sequence error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update sequence"
      );
    }
  };

  // RETURN
// =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };
  return (
    <Layout activePage="Insurance">
      <main className="dashboard-main">
        <div className="table-card">
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Insurance</span>

              <span className="rec-count ms-1">{total} items</span>
            </h3>

            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search ..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Insurance
            </button>
          </div>

          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Insurance Name</th>
                  <th className="um-th text-center">Details</th>
                  <th className="um-th text-center">Type</th>
                  <th className="um-th text-center">Sequence</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  Array.from({
                    length: 5,
                  }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({
                        length: 6,
                      }).map((_, cellIndex) => (
                        <td key={cellIndex} className="um-td text-center">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiPackage />
                      </div>

                      <div className="empty-title">
                        No insurance items found
                      </div>

                      <div className="empty-sub">
                        Use the Add Insurance button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.map((row, index) => (
                    <tr
                      key={row.insitem_id}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      <td className="um-td text-center">
                        <strong>{row.insitem_name}</strong>
                      </td>

                      <td className="um-td">
                        <div
                          style={{
                            maxWidth: "300px",
                            margin: "0 auto",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.insitem_desc || ""}
                        >
                          {row.insitem_desc || "—"}
                        </div>
                      </td>

                      <td className="um-td text-center">
                        <span
                          className={
                            Number(row.insitem_type) === 1
                              ? "badge badge-success"
                              : "badge badge-custom"
                          }
                        >
                          {Number(row.insitem_type) === 1 ? "Free" : "Paid"}
                        </span>
                      </td>

                      <td className="um-td text-center">
                        <select
                          name={`cmbsequence-${row.insitem_id}`}
                          className="form-select form-select-sm"
                          value={row.seq ?? 0}
                          onChange={(e) =>
                            handleSequenceChange(
                              row.insitem_id,
                              e.target.value
                            )
                          }
                        >
                          <option value="0">00</option>

                          {Array.from(
                            {
                              length: Math.max(total, 10),
                            },
                            (_, i) => i + 1
                          ).map((number) => (
                            <option key={number} value={number}>
                              {String(number).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="um-td text-center">
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
              
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={10}
            loading={loading}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />              
                            
          </div>
        </div>

        {formModal && (
          <div className="um-overlay" onClick={closeFormModal}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? <FiEdit size={24} /> : <FiPlus size={24} />}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId ? "Edit Insurance" : "Add Insurance"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Manage insurance information
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-8">
                    <label className="field-label">Insurance Name *</label>

                    <input
                      type="text"
                      className="rd-input"
                      style={{
                        textAlign: "start",
                      }}
                      placeholder="Enter insurance name"
                      maxLength={50}
                      value={form.insitem_name}
                      onChange={(e) =>
                        handleChange("insitem_name", e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="field-label">
                      Insurance Item Type
                    </label>

                    <select
                      className="rd-input"
                      value={form.insitem_type}
                      onChange={(e) =>
                        handleChange("insitem_type", e.target.value)
                      }
                    >
                      <option value="1">Free</option>
                      <option value="2">Paid</option>
                    </select>
                  </div>

                  <div className="col-md-12">
                    <label className="field-label">
                      Insurance Item Description
                    </label>

                    <textarea
                      className="rd-input"
                      style={{
                        textAlign: "start",
                      }}
                      rows="5"
                      placeholder="Enter insurance item description"
                      value={form.insitem_desc}
                      onChange={(e) =>
                        handleChange("insitem_desc", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Insurance
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Insurance
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {viewModal && (
          <div className="um-overlay" onClick={() => setViewModal(null)}>
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Insurance Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View insurance information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              <div className="um-modal-scroll">
                <div className="row g-3">
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      Insurance Name
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        {viewModal.insitem_name}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-2">
                      Insurance Item Type
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <span
                        className={
                          Number(viewModal.insitem_type) === 1
                            ? "badge badge-success"
                            : "badge badge-custom"
                        }
                      >
                        {Number(viewModal.insitem_type) === 1
                          ? "Free"
                          : "Paid"}
                      </span>
                    </div>
                  </div>

                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-2">
                      Insurance Item Description
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div
                        className="fw-semibold"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                        }}
                      >
                        {viewModal.insitem_desc ||
                          "No description available."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Insurance
                </button>
              </div>
            </div>
          </div>
        )}

        {delModal && (
          <div className="um-overlay" onClick={() => setDelModal(null)}>
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                <h2 className="delete-title">Delete Insurance?</h2>

                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.insitem_name}
                  </span>
                </div>

                <p className="delete-description">
                  This insurance item will be permanently deleted.
                  <br />
                  <strong>This action cannot be undone.</strong>
                </p>

                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        
      </main>
    </Layout>
  );
}
